import { useEffect, useState } from "react";
import { portalApi } from "@/lib/portalApi";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Wellness Assessment — renders whatever sections + questions the backend
 * returns from /portal/assessment. Supports four question types:
 *   scale    — 1..max numeric scale rendered as pills
 *   single   — radio-list
 *   multi    — checkbox list
 *   long_text — textarea
 *
 * On submit, only required questions are enforced (per the backend).
 */
export default function PortalAssessment() {
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [lastSubmittedAt, setLastSubmittedAt] = useState(null);
  const [submittedNow, setSubmittedNow] = useState(false);

  useEffect(() => {
    portalApi.get("/portal/assessment")
      .then(({ data }) => {
        setAssessment(data.assessment);
        if (data.last_submission?.submitted_at) {
          setLastSubmittedAt(data.last_submission.submitted_at);
        }
      })
      .catch(() => toast.error("Couldn't load the check-in. Please try again."));
  }, []);

  const setVal = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));

  const submit = async () => {
    if (!assessment) return;
    setSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(answers)
          .filter(([, v]) => v !== "" && v !== undefined && !(Array.isArray(v) && v.length === 0))
          .map(([question_id, value]) => ({ question_id, value })),
      };
      await portalApi.post("/portal/assessment/submit", payload);
      toast.success("Thank you for sharing.");
      setSubmittedNow(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!assessment) {
    return <div className="text-center py-12 text-[#1E3A8A]" data-testid="portal-assessment-loading">Loading…</div>;
  }

  if (submittedNow) {
    return (
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-8 text-center max-w-2xl mx-auto" data-testid="portal-assessment-completion">
        <div className="w-16 h-16 rounded-2xl bg-[#E8ECE7] text-[#166534] mx-auto mb-5 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8" strokeWidth={1.75} />
        </div>
        <h1 className="font-display text-2xl font-semibold text-[#0F172A]">Thank you for completing your check-in</h1>
        <div className="mt-4 text-sm text-[#334155] whitespace-pre-line text-left bg-[#F9F8F6] border border-[#E5E5E0] rounded-xl p-5">
          {assessment.completion || "A HarborWell Care Navigator will follow up with you soon."}
        </div>
        <button
          onClick={() => navigate("/portal", { replace: true })}
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554]"
          data-testid="portal-assessment-completion-home"
        >
          Back to home
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-assessment-page">
      <div>
        <p className="text-xs uppercase tracking-wider text-[#7E937D] font-medium">Wellness check-in</p>
        <h1 className="font-display text-3xl font-semibold text-[#0F172A] mt-1">{assessment.name}</h1>
        <p className="text-sm text-[#334155] mt-2">{assessment.intro}</p>
        {lastSubmittedAt && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#166534] bg-[#E8ECE7] px-3 py-1.5 rounded-full" data-testid="portal-assessment-last">
            <CheckCircle2 className="w-3.5 h-3.5" /> Last shared {new Date(lastSubmittedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </div>
        )}
      </div>

      {assessment.sections.map((sec, si) => (
        <section key={si} className="bg-white border border-[#E5E5E0] rounded-2xl p-5 space-y-5" data-testid={`portal-assessment-section-${si}`}>
          <h2 className="font-display text-lg font-semibold text-[#1E3A8A]">{sec.title}</h2>
          {sec.questions.map((q) => (
            <QuestionField key={q.id} q={q} value={answers[q.id]} onChange={(v) => setVal(q.id, v)} />
          ))}
        </section>
      ))}

      <div className="flex justify-end">
        <button
          onClick={submit}
          disabled={submitting}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] disabled:opacity-50"
          data-testid="portal-assessment-submit"
        >
          <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send to my Care Navigator"}
        </button>
      </div>
    </div>
  );
}

function QuestionField({ q, value, onChange }) {
  const required = q.required;
  const triggered = Array.isArray(q.crisis_trigger_values)
    && (Array.isArray(value)
          ? value.some((v) => q.crisis_trigger_values.includes(v))
          : q.crisis_trigger_values.includes(value));
  return (
    <div data-testid={`portal-q-${q.id}`}>
      <label className="block text-sm font-medium text-[#0F172A]">
        {q.text} {required && <span className="text-[#991B1B]">*</span>}
      </label>
      {q.type === "scale" && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {Array.from({ length: (q.max || 5) - (q.min || 1) + 1 }, (_, i) => i + (q.min || 1)).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={`w-12 h-12 rounded-2xl border text-base font-semibold transition-colors ${
                value === n
                  ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                  : "bg-white text-[#1E3A8A] border-[#E5E5E0] hover:border-[#1E3A8A]"
              }`}
              data-testid={`portal-q-${q.id}-${n}`}
            >
              {n}
            </button>
          ))}
          <div className="ml-2 text-xs text-[#334155] flex items-baseline gap-2">
            {q.min_label && <span>{q.min} = {q.min_label}</span>}
            {q.max_label && <span>{q.max} = {q.max_label}</span>}
          </div>
        </div>
      )}
      {q.type === "single" && (
        <div className="mt-2 space-y-2">
          {q.options.map((opt) => (
            <label key={opt} className="flex items-center gap-3 p-3 rounded-xl border border-[#E5E5E0] hover:bg-[#F9F8F6] cursor-pointer">
              <input
                type="radio"
                name={q.id}
                checked={value === opt}
                onChange={() => onChange(opt)}
                data-testid={`portal-q-${q.id}-opt`}
              />
              <span className="text-sm text-[#0F172A]">{opt}</span>
            </label>
          ))}
        </div>
      )}
      {q.type === "multi" && (
        <div className="mt-2 grid sm:grid-cols-2 gap-2">
          {q.options.map((opt) => {
            const arr = Array.isArray(value) ? value : [];
            const checked = arr.includes(opt);
            return (
              <label key={opt} className="flex items-center gap-3 p-3 rounded-xl border border-[#E5E5E0] hover:bg-[#F9F8F6] cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onChange(checked ? arr.filter((x) => x !== opt) : [...arr, opt])}
                  data-testid={`portal-q-${q.id}-opt`}
                />
                <span className="text-sm text-[#0F172A]">{opt}</span>
              </label>
            );
          })}
        </div>
      )}
      {q.type === "long_text" && (
        <textarea
          rows={4}
          maxLength={q.max_length || 2000}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-2 w-full px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm"
          placeholder="Share whatever you'd like…"
          data-testid={`portal-q-${q.id}-text`}
        />
      )}
      {triggered && q.crisis_message && (
        <div
          className="mt-3 bg-[#FEE2E2] border border-[#FCA5A5] rounded-xl p-4 text-sm text-[#0F172A]"
          data-testid={`portal-q-${q.id}-crisis`}
        >
          {q.crisis_message}
        </div>
      )}
    </div>
  );
}
